import type { DonationScheme } from "../services/api";
import type { DonationCampaign } from "./types";
import { DEFAULT_PRESET_AMOUNTS } from "./data";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=900&q=70&auto=format&fit=crop";

function trimOptionalText(value?: string | number | null): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).trim();
  return text || undefined;
}

function parseAmount(value?: string | number | null): number {
  if (value === "" || value === null) {
    return 0;
  }
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export function parseSchemeMediaUrlList(
  ...sources: (unknown | null | undefined)[]
): string[] {
  const urls: string[] = [];
  for (const raw of sources) {
    if (!raw) {
      continue;
    }
    const items = Array.isArray(raw) ? raw : [raw];
    for (const item of items) {
      if (typeof item === "string") {
        const trimmed = item.trim();
        if (trimmed.startsWith("[")) {
          try {
            const parsed = JSON.parse(trimmed) as unknown;
            if (Array.isArray(parsed)) {
              urls.push(...parseSchemeMediaUrlList(parsed));
              continue;
            }
          } catch {
            /* single URL */
          }
        }
        if (trimmed) {
          urls.push(trimmed);
        }
        continue;
      }
      if (item && typeof item === "object") {
        const record = item as { url?: string; file?: string; media?: string };
        const url = record.url ?? record.file ?? record.media;
        if (typeof url === "string" && url.trim()) {
          urls.push(url.trim());
        }
      }
    }
  }
  return [...new Set(urls)];
}

function firstMediaUrl(
  raw?: string[] | { url?: string; file?: string }[] | null
): string | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const first = raw[0];
  if (typeof first === "string" && first.trim()) {
    return first;
  }
  if (first && typeof first === "object") {
    const url = first.url ?? first.file;
    if (typeof url === "string" && url.trim()) {
      return url;
    }
  }
  return undefined;
}

export function getSchemeImageUrl(scheme: DonationScheme): string {
  if (typeof scheme.banner_image === "string" && scheme.banner_image.trim()) {
    return scheme.banner_image;
  }
  if (typeof scheme.banner === "string" && scheme.banner.trim()) {
    return scheme.banner;
  }
  return (
    firstMediaUrl(scheme.before_image) ??
    firstMediaUrl(scheme.media) ??
    FALLBACK_IMAGE
  );
}

function formatEventDate(dateStr?: string | null): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) {
      return undefined;
    }
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch {
    return undefined;
  }
}

export function getCampaignStatusLabel(dateStatus?: string | null): string {
  switch (dateStatus) {
    case "live":
      return "Live campaign";
    case "active":
      return "Active campaign";
    case "upcoming":
      return "Upcoming campaign";
    case "past":
      return "Campaign ended";
    default:
      return "Active campaign";
  }
}

function buildPresetAmounts(scheme: DonationScheme): number[] {
  const amountType = scheme.amount_type === "variable" ? "variable" : "fixed";
  if (amountType === "fixed") {
    const fixed = parseAmount(scheme.fixed_amount);
    if (fixed > 0) {
      return [fixed];
    }
    return DEFAULT_PRESET_AMOUNTS;
  }
  const min = parseAmount(scheme.min_contribution);
  const max = parseAmount(scheme.max_contribution);
  if (min > 0 && max > 0) {
    const inRange = DEFAULT_PRESET_AMOUNTS.filter(a => a >= min && a <= max);
    if (inRange.length > 0) {
      return inRange;
    }
    return [min, Math.round((min + max) / 2), max].filter(
      (v, i, arr) => arr.indexOf(v) === i
    );
  }
  return DEFAULT_PRESET_AMOUNTS;
}

export function schemeToCampaign(scheme: DonationScheme): DonationCampaign {
  const goal = parseAmount(scheme.amount_needed);
  const raised = parseAmount(scheme.amount_collected);
  const dateStatus = scheme.date_status ?? undefined;
  const isLive =
    dateStatus === "active" ||
    dateStatus === "live" ||
    dateStatus === "upcoming" ||
    (!dateStatus && scheme.is_active !== false);

  return {
    id: String(scheme.id),
    title: scheme.title,
    subTitle: scheme.sub_title ?? undefined,
    description:
      trimOptionalText(scheme.description) ??
      trimOptionalText(scheme.sub_title) ??
      "Support this initiative through your generous contribution.",
    image: getSchemeImageUrl(scheme),
    categoryId: String(scheme.category ?? ""),
    categoryName: trimOptionalText(scheme.category_name) ?? "General",
    raised,
    goal,
    featured: isLive && goal > 0,
    eventDate: formatEventDate(scheme.start_date),
    dateStatus,
    amountType: scheme.amount_type === "variable" ? "variable" : "fixed",
    fixedAmount: parseAmount(scheme.fixed_amount) || undefined,
    minContribution: parseAmount(scheme.min_contribution) || undefined,
    maxContribution: parseAmount(scheme.max_contribution) || undefined,
    presetAmounts: buildPresetAmounts(scheme),
    typeOfDonationName: scheme.type_of_donation_name ?? undefined,
    institutionName: trimOptionalText(scheme.institution_name),
    subCategory:
      trimOptionalText(scheme.sub_category_title) ??
      trimOptionalText(scheme.sub_category),
    startDate: scheme.start_date ?? undefined,
    endDate: scheme.end_date ?? undefined,
    beforeDetails: trimOptionalText(scheme.before_details),
    afterDetails: trimOptionalText(scheme.after_details),
    beforeMedia: parseSchemeMediaUrlList(
      scheme.before_media,
      scheme.before_image
    ),
    afterMedia: parseSchemeMediaUrlList(scheme.after_media, scheme.after_image),
    allowDonationAfterTarget: scheme.allow_donation_after_target,
    isCashDonation: scheme.is_cash_donation,
    isActive: scheme.is_active !== false,
  };
}

export function schemesToCampaigns(
  schemes: DonationScheme[]
): DonationCampaign[] {
  return schemes.map(schemeToCampaign);
}
